import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { Formik, Form, FieldArray, Field } from 'formik';
import { School, ApiObject, CommonHash, Label } from 'app/types';
import { useNavigate, useParams } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import {
  useGetBackofficeNewSchoolQuery,
  useAddSchoolMutation,
  usePatchSchoolMutation,
  useGetSchoolEditQuery,
  useGetCitiesSearchQuery,
  useGetTagsQuery,
} from 'services/exploreApi';
import styles from './SchoolForm.module.scss';
import Loader from 'common/components/Loader/Loader';
import Text from 'common/components/Text/Text';

const SchoolForm = (): React.ReactElement => {
  const { id } = useParams();
  let school: ApiObject<School> | undefined;
  let schoolLoading;
  if (id) {
    ({ data: school, isLoading: schoolLoading } = useGetSchoolEditQuery(parseInt(id || '')));
  } else {
    ({ data: school, isLoading: schoolLoading } = useGetBackofficeNewSchoolQuery());
  }
  const { data: { data: cities } = {}, isLoading: citiesLoading } = useGetCitiesSearchQuery();
  const [city, setCity] = useState(
    school?.data.city_id
      ? {
          label: cities?.find((c) => c.id === school?.data.city_id)?.name || '',
          value: school?.data.city_id,
        }
      : 'Search city'
  );
  const { data: { data: tags } = {}, isLoading: tagsLoading } = useGetTagsQuery();
  const [addSchool] = useAddSchoolMutation();
  const [patchSchool] = usePatchSchoolMutation();
  const initialValues: School = { ...(school?.data || ({} as School)) };
  const navigate = useNavigate();
  useEffect(() => {
    if (school?.data.city_id) {
      setCity({
        label: cities?.find((c) => c.id === school?.data.city_id)?.name || '',
        value: school?.data.city_id,
      });
    } else {
      setCity('Search city');
    }
  }, [school, cities]);
  if (schoolLoading) return <Loader />;
  return (
    <div>
      <h1>{id ? `Editing ${school?.data.name}` : 'New school'}</h1>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={async (values) => {
          // parseFunction(values);
          if (id) {
            await patchSchool({ school_id: id, data: values });
            navigate(`/backoffice/schools/${id}`);
          } else {
            const result = await addSchool(values);
            if (result) {
              // navigate(`/backoffice/schools/${result.data.id}`);
              navigate(`/backoffice/schools`);
            }
          }
        }}
      >
        {({ setFieldValue, handleChange, values }) => (
          <Form className={styles.form}>
            <div className={styles.group}>
              <label htmlFor="school[name]">Name</label>
              <input
                name="school[name]"
                placeholder="Name"
                onChange={handleChange}
                defaultValue={school?.data.name || ''}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[city_id]">City</label>
              <Select
                isSearchable
                classNamePrefix="react-select"
                className={styles.filtersFormSelect}
                placeholder="Search City"
                options={cities?.map((c) => ({ label: c.name, value: c.id })) || []}
                isLoading={citiesLoading}
                onChange={(c: any) => {
                  setCity(c);
                  setFieldValue('school[city_id]', c.value);
                }}
                value={city}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[is_partner]">Is partner</label>
              <select onChange={handleChange} name="school[is_partner]">
                <option selected={String(school?.data.is_partner) === 'nil'} value="nil" />
                <option selected={String(school?.data.is_partner) === 'true'} value="true">
                  True
                </option>
                <option selected={String(school?.data.is_partner) === 'false'} value="false">
                  False
                </option>
              </select>
            </div>
            <div className={styles.group}>
              <label htmlFor="school[number_of_students]">Number of students</label>
              <input
                name="school[number_of_students]"
                placeholder="Number of students"
                defaultValue={school?.data.number_of_students || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[number_of_international_students]">
                Number of international students
              </label>
              <input
                name="school[number_of_international_students]"
                type="number"
                placeholder="Number of international students"
                defaultValue={school?.data.number_of_international_students || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[employability_score]">Employability Score</label>
              <input
                name="school[employability_score]"
                type="number"
                placeholder="Employability Score"
                defaultValue={school?.data.employability_score || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[average_salary]">Overview</label>
              <textarea
                name="school[overview]"
                placeholder="Overview"
                defaultValue={school?.data.overview || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[about]">About</label>
              <textarea
                name="school[about]"
                placeholder="About"
                defaultValue={school?.data.about || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[website]">Website</label>
              <input
                name="school[website]"
                placeholder="Website"
                defaultValue={school?.data.website || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[twitter_link]">Twitter link</label>
              <input
                name="school[twitter_link]"
                placeholder="Twitter link"
                defaultValue={school?.data.twitter_link || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[facebook_link]">Facebook link</label>
              <input
                name="school[facebook_link]"
                placeholder="Facebook link"
                defaultValue={school?.data.facebook_link || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[why_study_here]">Why study here</label>
              <input
                name="school[why_study_here]"
                placeholder="Why study here"
                defaultValue={school?.data.why_study_here || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[scholarship_offer]">Scholarship offer</label>
              <select name="school[scholarship_offer]" onChange={handleChange}>
                <option selected={String(school?.data.scholarship_offer) === 'nil'} value="nil" />
                <option selected={String(school?.data.scholarship_offer) === 'true'} value="true">
                  True
                </option>
                <option selected={String(school?.data.scholarship_offer) === 'false'} value="false">
                  False
                </option>
              </select>
            </div>
            <div className={styles.group}>
              <label htmlFor="school[acommodation_offer]">Accommodation offer</label>
              <select name="school[accommodation_offer]" onChange={handleChange}>
                <option selected={String(school?.data.accommodation_offer) === 'nil'} value="nil" />
                <option selected={String(school?.data.accommodation_offer) === 'true'} value="true">
                  True
                </option>
                <option
                  selected={String(school?.data.accommodation_offer) === 'false'}
                  value="false"
                >
                  False
                </option>
              </select>
            </div>
            <div className={styles.group}>
              <label htmlFor="school[established_year]">Established year</label>
              <input
                name="school[established_year]"
                type="number"
                placeholder="Established year"
                defaultValue={school?.data.established_year || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[average_tuition]">Average Tuition</label>
              <input
                name="school[average_tuition]"
                type="number"
                placeholder="Average Tuition"
                defaultValue={school?.data.average_tuition || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[average_program_year]">Average program year</label>
              <input
                name="school[average_program_year]"
                type="number"
                placeholder="Average program year"
                defaultValue={school?.data.average_program_year || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[priority]">Priority</label>
              <input
                name="school[priority]"
                type="number"
                placeholder="Priority"
                defaultValue={school?.data.priority || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[position_ranking]">Position Ranking</label>
              <input
                name="school[position_ranking]"
                type="number"
                placeholder="Position Ranking"
                defaultValue={school?.data.position_ranking || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[ranking_name]">Ranking Name</label>
              <input
                name="school[ranking_name]"
                placeholder="Ranking Name"
                defaultValue={school?.data.ranking_name || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[description]">Description</label>
              <textarea
                name="school[description]"
                placeholder="Description"
                defaultValue={school?.data.description || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[acceptance_rate]">Acceptance Rate</label>
              <select
                name="school[acceptance_rate]"
                onChange={handleChange}
                defaultValue={school?.data.acceptance_rate || ''}
              >
                <option value="nil" />
                <option value="high">High</option>
                <option value="moderate">Moderate</option>
                <option value="competitive">Competitive</option>
              </select>
            </div>
            <div className={styles.group}>
              <label htmlFor="school[international_student_description]">
                International Student Description
              </label>
              <textarea
                name="school[international_student_description]"
                placeholder="International Student Description"
                defaultValue={school?.data.international_student_description || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[learning_and_teaching_description]">
                Learning and Teaching Description
              </label>
              <textarea
                name="school[learning_and_teaching_description]"
                placeholder="Learning and Teaching Description"
                defaultValue={school?.data.learning_and_teaching_description || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[application_description]">Application description</label>
              <textarea
                name="school[application_description]"
                placeholder="Application description"
                defaultValue={school?.data.application_description || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[working_after_months]">Working after months</label>
              <textarea
                name="school[working_after_months]"
                placeholder="Working after months"
                defaultValue={school?.data.working_after_months || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[working_after_percentage]">Working after percentage</label>
              <textarea
                name="school[working_after_percentage]"
                placeholder="Application description"
                defaultValue={school?.data.working_after_percentage || ''}
                onChange={handleChange}
              />
            </div>
            {/* <div className={styles.group}>
              <label htmlFor="school[alumni_information]">Alumni Information</label>
              <textarea
                name="school[alumni_information]"
                placeholder="Alumni Information"
                defaultValue={school?.data.alumni_information || undefined}
                onChange={handleChange}
              />
            </div> */}
            <div className={styles.group}>
              <label htmlFor="school[career_service_description]">Career Service Description</label>
              <textarea
                name="school[career_service_description]"
                placeholder="Career Service Description"
                defaultValue={school?.data.career_service_description || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[student_support_description]">
                Student Support Description
              </label>
              <textarea
                name="school[student_support_description]"
                placeholder="Student Support Description"
                defaultValue={school?.data.student_support_description || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[application_fee]">Application fee</label>
              <textarea
                name="school[application_fee]"
                placeholder="Application fee"
                defaultValue={school?.data.application_fee || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[application_fee_currency]">Application fee currency</label>
              <select
                name="school[application_fee_currency]"
                defaultValue={school?.data.application_fee_currency || 'nil'}
                onChange={handleChange}
              >
                <option value="nil" />
                <option value="USD">USD</option>
                <option value="RMB">RMB</option>
                <option value="GBP">GBP</option>
                <option value="EUR">EUR</option>
                <option value="NOK">NOK</option>
                <option value="HUF">HUF</option>
                <option value="CHF">CHF</option>
                <option value="MYR">MYR</option>
                <option value="AED">AED</option>
                <option value="SGD">SGD</option>
                <option value="PLN">PLN</option>
                <option value="SEK">SEK</option>
                <option value="ISK">ISK</option>
                <option value="BAM">BAM</option>
                <option value="BHD">BHD</option>
                <option value="CZK">CZK</option>
                <option value="CAD">CAD</option>
                <option value="AUD">AUD</option>
              </select>
            </div>
            <div className={styles.group}>
              <label htmlFor="school[is_visible]">Is visible</label>
              <select onChange={handleChange} name="school[is_visible]">
                <option selected={String(school?.data.is_visible) == 'nil'} value="nil" />
                <option selected={String(school?.data.is_visible) == 'true'} value="true">
                  True
                </option>
                <option selected={String(school?.data.is_visible) == 'false'} value="false">
                  False
                </option>
              </select>
            </div>
            <div className={styles.group}>
              <label htmlFor="school[employability_time]">Employability time</label>
              <input
                name="school[employability_time]"
                placeholder="Employability time"
                defaultValue={school?.data.employability_time || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[assessment_text]">Assessment text</label>
              <textarea
                name="school[assessment_text]"
                placeholder="Assessment text"
                defaultValue={school?.data.assessment_text || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[requirement_document_description]">
                Requirement document description
              </label>
              <textarea
                name="school[requirement_document_description]"
                placeholder="Requirement document description"
                defaultValue={school?.data.requirement_document_description || ''}
                onChange={handleChange}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="school[partner_type]">Partner type</label>
              <select
                onChange={handleChange}
                name="school[partner_type]"
                defaultValue={school?.data.partner_type || 'nil'}
              >
                <option value="nil" />
                <option value="directly">Directly</option>
                <option value="indirectly">Indirectly</option>
              </select>
            </div>
            <Text size={24} weight={800}>
              Labels
            </Text>
            <FieldArray
              name="labels"
              render={(arrayHelpers) => (
                <div>
                  {values.labels && values.labels.length > 0 ? (
                    values.labels.map(
                      (label: CommonHash, index: number) =>
                        !label._destroy && (
                          <div key={index}>
                            <div className="d-flex gap-3 align-items-center mt-2">
                              <Text size={22}> Label {index + 1} </Text>
                              <Button
                                type="button"
                                variant="danger"
                                className={styles.deleteButton}
                                onClick={() => {
                                  setFieldValue(`labels[${index}]._destroy`, true);
                                }}
                              >
                                Delete
                              </Button>
                            </div>
                            <div className={styles.group}>
                              <label htmlFor={`labels[${index}].city_id`}>Tag</label>
                              <Select
                                isSearchable
                                classNamePrefix="react-select"
                                className={styles.filtersFormSelect}
                                placeholder="Search Tag"
                                options={
                                  tags?.map((t) => ({
                                    label: t.name,
                                    value: t.id,
                                    description: t.description,
                                  })) || []
                                }
                                isLoading={tagsLoading}
                                onChange={({ value, description }: any) => {
                                  setFieldValue(`labels[${index}].tag_id`, value);
                                  setFieldValue(`labels[${index}].description`, description);
                                }}
                                value={tags
                                  ?.map((tag) => ({ label: tag.name, value: tag.id }))
                                  .find(
                                    (tag) =>
                                      tag.value === (values.labels[index]?.tag_id || 'Search tags')
                                  )}
                              />
                            </div>
                            <div className={styles.group}>
                              <label htmlFor={`labels[${index}].description`}>Description</label>
                              <Field name={`labels[${index}].description`} as="textarea" />
                            </div>
                            {index === values.labels.length - 1 && (
                              <Button
                                type="button"
                                className="mt-3"
                                variant="success"
                                onClick={() =>
                                  arrayHelpers.push({
                                    description: '',
                                  })
                                }
                              >
                                + Label
                              </Button>
                            )}
                          </div>
                        )
                    )
                  ) : (
                    <Button
                      type="button"
                      className="mt-3"
                      variant="success"
                      onClick={() =>
                        arrayHelpers.push({
                          description: '',
                        })
                      }
                    >
                      Add a Label
                    </Button>
                  )}
                </div>
              )}
            />
            <Button type="submit" variant="primary">
              {id ? 'Update' : 'Create'}
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default SchoolForm;
